import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { useHistory } from 'react-router-dom';

import { UserRole } from '../../backendsdk';
import { TrackedButton as Button, TrackedDialog as Dialog } from '../../components/TrackedComponents';
import useApi from '../../hooks/api';
import useIsMobile from '../../hooks/isMobile';
import useProfile from '../../hooks/profile';
import { eventFeedPageDefs, isAllowedCustomer } from '../../utils/Pages';
import { getStorageItem, setStorageItem } from '../../utils/Storage';
import { unformatPhoneNumber } from '../../utils/Str';
import { PHONE_NUMBER_MASK_MAP } from '../content/UserSettings/UserSettings';
import { FLEET_ROLES } from '../content/Users/UsersComponent';
import { isValidPhoneNumber } from '../content/Users/validation';

export const SKIP_FEED_POPUP_KEY = 'OE-SkipFeedPopup';

const FeedPopupComponent: React.FC = () => {
    const { profile } = useProfile();
    const { api } = useApi();
    const [open, setOpen] = useState<boolean>(true);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [phoneNumberError, setPhoneNumberError] = useState<string>('');
    const skipPopup = getStorageItem(SKIP_FEED_POPUP_KEY) || '0';
    const [skipFeedPopup, setSkipFeedPopup] = useState<boolean>(false);
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const history = useHistory();

    const fleetRoles: UserRole[] = FLEET_ROLES.filter((role) => role !== UserRole.FleetOperationsControl);

    if (!isAllowedCustomer(eventFeedPageDefs, profile) || skipPopup == '1' || !fleetRoles.includes(profile.role)) {
        return null;
    }

    const onClose = () => {
        setStorageItem(SKIP_FEED_POPUP_KEY, skipFeedPopup ? '1' : '0', 365 * 24); // 1 year
        setOpen(false);
    };

    const onSendLink = () => {
        if (!profile.phone_number && !isValidPhoneNumber(phoneNumber)) {
            setPhoneNumberError(t('content.user_settings.user_details.phone_number_required'));
        } else {
            api.apiV0FeedLinkPost(
                phoneNumber ? { feedLinkRequest: { phone_number: unformatPhoneNumber(phoneNumber) } } : {},
            ).then(onClose);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const phoneInput: any = (props: any) => {
        const inputElement = (
            <TextField
                id="phone-input"
                label={t('content.user_settings.user_details.phone_number')}
                fullWidth
                size="small"
                error={!!phoneNumberError}
                helperText={phoneNumberError}
                inputProps={{ 'data-testid': 'phone-number-input', direction: 'ltr' }}
                {...props}
            />
        );
        return inputElement;
    };

    return (
        <Dialog id="feed-popup" open={open} onClose={onClose}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 2 }}>
                {!!profile.phone_number || isMobile ? (
                    <>
                        <Box>
                            <Box>{t('content.feed.popup.header_text')}</Box>
                            <Box>{t('content.feed.popup.phone_exists_text')}</Box>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box>
                            <Box>{t('content.feed.popup.header_text')}</Box>
                            <Box>{t('content.feed.popup.no_phone_text')}</Box>
                        </Box>
                        <InputMask
                            mask={PHONE_NUMBER_MASK_MAP[profile.customer.settings.country]}
                            value={phoneNumber}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                            maskChar=""
                        >
                            {phoneInput}
                        </InputMask>
                    </>
                )}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        id="close-feed-popup-button"
                        data-testid="close-feed-popup-button"
                        variant="contained"
                        color="primary"
                        onClick={onClose}
                    >
                        {t('content.feed.popup.close')}
                    </Button>
                    {isMobile ? (
                        <Button
                            id="take-me-there-button"
                            data-testid="take-me-there-button"
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                history.push(eventFeedPageDefs.path[eventFeedPageDefs.path.length - 1]);
                                onClose();
                            }}
                        >
                            {t('content.feed.popup.take_me_there')}
                        </Button>
                    ) : (
                        <Button
                            id="send-link-button"
                            data-testid="send-link-button"
                            variant="contained"
                            color="secondary"
                            onClick={onSendLink}
                            disabled={!profile.phone_number && !unformatPhoneNumber(phoneNumber)}
                        >
                            {t('content.feed.popup.send_link')}
                        </Button>
                    )}
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="skip-feed-popup-checkbox"
                            data-testid="skip-feed-popup-checkbox"
                            checked={skipFeedPopup}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSkipFeedPopup(e.target.checked)}
                            disableRipple
                        />
                    }
                    label={<Typography>{t('content.feed.popup.skip')}</Typography>}
                />
            </Box>
        </Dialog>
    );
};

export default FeedPopupComponent;
